import type { ApolloClient } from '@apollo/client';
import { type GameLobbyRefusalKind, gameLobbyRefusalKind } from '@words-happen/app-urls';
import {
  CancelGame,
  type CancelGameMutation,
  type CancelGameMutationVariables,
  CreateGame,
  type CreateGameMutation,
  GameResult,
  GameStatus,
  type GameSummaryFieldsFragment,
  InviteToGame,
  type InviteToGameMutation,
  type InviteToGameMutationVariables,
  JoinGame,
  type JoinGameMutation,
  type JoinGameMutationVariables,
  type LobbyGameFieldsFragment,
  MyGames,
  type MyGamesQuery,
  QuickMatch,
  type QuickMatchMutation,
} from '../../generated/graphql.api-app';
import { apolloClient } from '../apollo-client';
import { graphQLError } from '../profile';
import { isUnauthenticatedError } from '../settings';

/** A game in the games list. Built from the game's own columns: never its board or racks. */
export interface GamesListGame {
  id: string;
  quickMatch: boolean;
  isMyTurn: boolean;
  /** Null while the game waits for an opponent. */
  opponentName: string | null;
  myScore: number;
  opponentScore: number;
  /** Set once the game has finished. */
  result: 'won' | 'lost' | 'drew' | null;
  /** ISO 8601; moves on with every seat, turn and cancellation. */
  updatedAt: string;
}

/** The signed-in player's games: active (their turn first), waiting, and finished in the last 14 days. */
export interface GamesList {
  active: GamesListGame[];
  waiting: GamesListGame[];
  finished: GamesListGame[];
}

/** A game the lobby has just created, joined or cancelled. */
export interface LobbyGame {
  id: string;
  status: 'waiting' | 'active' | 'finished' | 'cancelled';
  isMyTurn: boolean;
  /** Null while the game waits for an opponent. */
  opponentName: string | null;
}

/**
 * Why a lobby request failed:
 * - a refusal (`GameLobbyRefusalKind`), with the server's message, written for the player
 * - `unauthenticated`: the session has expired
 * - `failed`: anything else, such as the network
 */
export type LobbyFailure =
  | { kind: GameLobbyRefusalKind; message: string }
  | { kind: 'unauthenticated' }
  | { kind: 'failed' };

/** What the player reads for a failure that isn't a refusal. */
export const LOBBY_FAILED_MESSAGE = "That didn't work. Check your connection and try again.";

export const lobbyFailure = (error: unknown): LobbyFailure => {
  if (isUnauthenticatedError(error)) {
    return { kind: 'unauthenticated' };
  }

  const graphQL = graphQLError(error);
  const kind = gameLobbyRefusalKind(graphQL?.code);
  return kind ? { kind, message: graphQL?.message || LOBBY_FAILED_MESSAGE } : { kind: 'failed' };
};

/** The message a failure shows the player. */
export const lobbyFailureMessage = (failure: LobbyFailure): string =>
  'message' in failure ? failure.message : LOBBY_FAILED_MESSAGE;

export const GAME_RESULTS: Record<GameResult, GamesListGame['result']> = {
  [GameResult.Won]: 'won',
  [GameResult.Lost]: 'lost',
  [GameResult.Drew]: 'drew',
};

export const GAME_STATUSES: Record<GameStatus, LobbyGame['status']> = {
  [GameStatus.Waiting]: 'waiting',
  [GameStatus.Active]: 'active',
  [GameStatus.Finished]: 'finished',
  [GameStatus.Cancelled]: 'cancelled',
};

const fromSummary = (game: GameSummaryFieldsFragment): GamesListGame => ({
  id: game.id,
  quickMatch: game.quickMatch,
  isMyTurn: game.isMyTurn,
  opponentName: game.opponent?.displayName ?? null,
  myScore: game.me.score,
  opponentScore: game.opponent?.score ?? 0,
  result: game.me.result ? GAME_RESULTS[game.me.result] : null,
  updatedAt: String(game.updatedAt),
});

const fromLobbyGame = (game: LobbyGameFieldsFragment): LobbyGame => ({
  id: game.id,
  status: GAME_STATUSES[game.status],
  isMyTurn: game.isMyTurn,
  opponentName: game.players.find(({ isMe }) => !isMe)?.displayName ?? null,
});

const required = <T>(data: T | null | undefined, operation: string): T => {
  if (!data) {
    throw new Error(`${operation} returned no data`);
  }
  return data;
};

/** The board game lobby on words-happen-gql (WH-155). Every call rejects on failure; `lobbyFailure` explains why. */
export interface GamesApi {
  myGames: () => Promise<GamesList>;
  /** A new game waiting for an invited opponent. */
  createGame: () => Promise<LobbyGame>;
  /** The invite code of the caller's waiting game: the same each time. */
  inviteCode: (gameId: string) => Promise<string>;
  joinGame: (code: string) => Promise<LobbyGame>;
  /** `active` when matched with a waiting player; `waiting` when the caller's game waits for the next. */
  quickMatch: () => Promise<LobbyGame>;
  cancelGame: (gameId: string) => Promise<LobbyGame>;
}

export const createGamesApi = (client: ApolloClient = apolloClient): GamesApi => ({
  myGames: async () => {
    const { data } = await client.query<MyGamesQuery>({ query: MyGames, fetchPolicy: 'network-only' });
    const { active, waiting, finished } = required(data, 'myGames').myGames;
    return { active: active.map(fromSummary), waiting: waiting.map(fromSummary), finished: finished.map(fromSummary) };
  },
  createGame: async () => {
    const { data } = await client.mutate<CreateGameMutation>({ mutation: CreateGame });
    return fromLobbyGame(required(data, 'createGame').createGame);
  },
  inviteCode: async (gameId) => {
    const { data } = await client.mutate<InviteToGameMutation, InviteToGameMutationVariables>({
      mutation: InviteToGame,
      variables: { gameId },
    });
    return required(data, 'inviteToGame').inviteToGame.code;
  },
  joinGame: async (code) => {
    const { data } = await client.mutate<JoinGameMutation, JoinGameMutationVariables>({
      mutation: JoinGame,
      variables: { code },
    });
    return fromLobbyGame(required(data, 'joinGame').joinGame);
  },
  quickMatch: async () => {
    const { data } = await client.mutate<QuickMatchMutation>({ mutation: QuickMatch });
    return fromLobbyGame(required(data, 'quickMatch').quickMatch);
  },
  cancelGame: async (gameId) => {
    const { data } = await client.mutate<CancelGameMutation, CancelGameMutationVariables>({
      mutation: CancelGame,
      variables: { gameId },
    });
    return fromLobbyGame(required(data, 'cancelGame').cancelGame);
  },
});
